import type Demand from "@/domains/Demand/Demand";
import Title_ru_RU from "@/core/misc/decorators/Title_ru_RU";
import serializeSimplified from '@/core/misc/decorators/SerializeSimplified';
import TYPES from '@/core/services/Changelogs/types';
import Core from "@/core/Core";
import BaseEntity from "@/core/misc/BaseEntity";
import DateRange from '@/core/types/DateRange';
import Product from '@/domains/Products/Product';
import ProductCategory from "@/domains/ProductCategories/ProductCategory";
import Store from "@/domains/Stores/Store";
import Partner from "@/domains/Partners/Partner";
import ProductionSite from "@/domains/ProductionSites/ProductionSite";
import Company from "@/domains/Companies/Company";
import Person from "@/domains/Persons/Person";
import Unit from "@/domains/Units/Unit";
import Qty from "@/core/types/Qty";
import VatRate from "@/core/types/VatRate";

class AbstractDemand extends BaseEntity {
  /** Покупатель */
  @serializeSimplified
  buyer: Partner;

  /** Компания покупателя */
  @Title_ru_RU('юр лицо', TYPES.OBJECT)
  @serializeSimplified
  buyerCompany: Company;

  /** Склад покупателя */
  @Title_ru_RU('склад', TYPES.OBJECT)
  @serializeSimplified
  buyerStore: Store;

  /** Производственная площадка покупателя */
  @Title_ru_RU('производственная площадка', TYPES.OBJECT)
  @serializeSimplified
  buyerProductionSite: ProductionSite;

  /** Ответственный человек со стороны покупателя */
  @Title_ru_RU('ответственный', TYPES.OBJECT)
  @serializeSimplified
  responsibleBuyer: Person;

  /** Ставка НДС покупателя */
  @Title_ru_RU('ставка НДС', TYPES.NUMBER)
  vatRateBuyer: VatRate;
  /** НДС включен */
  isVatIncludedBuyer: boolean = true;

  /** Категория товара */
  @serializeSimplified
  private _productCategory: ProductCategory;

  /** Товар */
  @serializeSimplified
  private _product: Product;

  /** Желаемое кол-во */
  @Title_ru_RU('желаемое кол-во', TYPES.NUMBER)
  qtyDesired: Qty;
  /** Кол-во уже заказаное по этой потребности */
  @Title_ru_RU('заказанное кол-во', TYPES.NUMBER)
  qtyOrdered: Qty;
  /** Кол-во осталось заказать */
  @Title_ru_RU('осталось заказать кол-во', TYPES.NUMBER)
  qtyLeft: Qty;
  /** Единица измерения */
  @Title_ru_RU('единица измерения', TYPES.OBJECT)
  @serializeSimplified
  unit: Unit;

  /** Предзаказ ли это? */
  isPreorderNeeded: boolean = false;
  /** Минимальный месячный резерв */
  @Title_ru_RU('страховой запас на складе', TYPES.NUMBER)
  preorderMonthlyReserve: Qty = Qty.fromEmpty();
  /** Период выборки по предзаказу */
  @Title_ru_RU('период поставки', TYPES.DATE_RANGE)
  preorderShipmentPeriod: DateRange | null = new DateRange();

  /** константа для детей Demand & DemandV2 */

  static readonly DELIVERY_DATE_WARNING_DAYS = 3;

  static fromServer(data: any, result = new AbstractDemand()): AbstractDemand {
    super.fromServer(data, result);

    result.buyer = Core.utils.hydrate(data.buyer, Partner.fromServer)!;
    result.buyerCompany = Core.utils.hydrate(data.buyerCompany, Company.fromServer)!;
    result.buyerStore = Core.utils.hydrate(data.buyerStore, Store.fromServer)!;
    result.buyerProductionSite = Core.utils.hydrate(data.buyerProductionSite, ProductionSite.fromServer)!;
    result.responsibleBuyer = Core.utils.hydrate(data.responsibleBuyer, Person.fromServer)!;

    result.vatRateBuyer = Core.utils.hydrate(data.vatRateBuyer, VatRate.fromServer)!;

    result._productCategory = Core.utils.hydrate(data.productCategory, ProductCategory.fromServer)!;
    result._product = Core.utils.hydrate(data.product, Product.fromServer)!;

    result.qtyDesired = Core.utils.hydrate(data.qtyDesired, Qty.fromServer)!;
    result.qtyOrdered = Core.utils.hydrate(data.qtyOrdered, Qty.fromServer)!;
    result.qtyLeft = Core.utils.hydrate(data.qtyLeft, Qty.fromServer)!;
    result.unit = Core.utils.hydrate(data.unit, Unit.fromServer)!;

    result.preorderMonthlyReserve = Core.utils.hydrate(data.preorderMonthlyReserve, Qty.fromServer) ?? Qty.fromEmpty();
    result.preorderShipmentPeriod = Core.utils.hydrate(data.preorderShipmentPeriod, DateRange.fromServer) ?? new DateRange();

    return result;
  }

  static fromNew(result = new AbstractDemand()): AbstractDemand {
    if (Core.currentPartner.isBuyer) {
      result.buyer = Core.currentPartner;
      result.responsibleBuyer = Core.currentUser;

      /** Юр. лицо по умолчанию. Конкретные id зашиты здесь временно: убрать,
       *  когда у партнёра появится поле дефолтной компании. */
      /** Если это сибирское здоровье */
      if (result.buyer.isLSZ) {
        result.buyerCompany = result.buyer.companies.find(_company => _company.id === 66) as Company;
      /** Если это наманган */
      } else if (result.buyer.isNamangan) {
        const namanganBuyerCompany = result.buyer.companies.find(_company => _company.id === 495) as Company;

        result.buyerCompany = namanganBuyerCompany || result.buyer.companies[0];
      } else if (Core.utils.isNotEmptyArray(result.buyer.companies) && result.buyer.companies.length === 1) {
        result.buyerCompany = result.buyer.companies[0];
      }

      /** Склад доставки. Главный — закреплённый за юр. лицом, поэтому и
       *  считается после него. Остальные ветки — тот же временный хардкод. */
      if (Core.utils.isNullOrUndefined(result.buyerCompany?.defaultStore) === false) {
        result.buyerStore = result.buyerCompany.defaultStore!;
      } else if (result.buyer.isLSZ) {
        result.buyerStore = Store.fromServer(
          result.buyer.stores.find(_store => _store.id === 47) || result.buyer.stores[0]
        );
      } else if (Core.utils.isNotEmptyArray(result.buyer.stores) && result.buyer.stores.length === 1) {
        result.buyerStore = result.buyer.stores[0];
      }
    }

    return result;
  }

  static fromAnotherDemand(anotherDemand: Demand, result = new AbstractDemand()): AbstractDemand {

    result.buyer = Core.utils.cloneDeep(anotherDemand.buyer);
    result.buyerCompany = Core.utils.cloneDeep(anotherDemand.buyerCompany);
    result.buyerStore = Core.utils.cloneDeep(anotherDemand.buyerStore);
    result.buyerProductionSite = Core.utils.cloneDeep(anotherDemand.buyerProductionSite);
    result.responsibleBuyer = Core.utils.cloneDeep(anotherDemand.responsibleBuyer);

    /** Наследуемой пачке тоже необходимо быть предзаказом, иначе будут требовать дату, а не период */
    result.isPreorderNeeded = anotherDemand.isPreorderNeeded;
    result.preorderShipmentPeriod = Core.utils.cloneDeep(anotherDemand.preorderShipmentPeriod);

    return result;
  }

  /** Страховой запас не может превышать желаемое количество */
  validatePreorderMonthlyReserve(value: Qty): string | null {
    if (value?.isPositive()
      && this.qtyDesired?.isPositive()
      && value.greaterThan(this.qtyDesired)) {
      return 'Не может быть больше общего количества';
    }
    else {
      return null;
    }
  }


  /** ================ Геттеры/сеттеры ================ */

  set product(value: Product) {
    this._product = value;
    this.productCategory = value?.category!;
    this.unit = value?.unit!;
  }

  get product() {
    return this._product;
  }

  set productCategory(value: ProductCategory) {
    this._productCategory = value;
    if (value?.vatRate) {
      this.vatRateBuyer = value?.vatRate!;
    }
  }

  get productCategory() {
    return this._productCategory;
  }

  /**
   * Название товара для колонки.
   * Продавец видит товарную категорию, покупатель — конкретный товар.
   */
  get productName(): string {
    if (Core.currentPartner.isSeller) {
      return this.productCategory?.name
        || this.product?.category?.name
        || '--';
    }
    return this.product?.name || '--';
  }
}

export default AbstractDemand;